import axios from 'axios';

import { AdrMeta, ProjectKnowledge, ProjectKnowledgeType } from '../../entities/project-knowledge';
import { auth } from '../firebase/firebase';
import { API_BASE_URL } from './index';

const getHeaders = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

export const knowledgeErrorMessage = (err: unknown): string | undefined =>
  axios.isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;

// Unwrapped array response (unlike e.g. standalone comments' `{comments: [...]}`) — the gateway
// controller returns `listDocuments()`'s result directly.
export const listProjectKnowledge = async (projectId: string): Promise<ProjectKnowledge[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<ProjectKnowledge[]>(`${API_BASE_URL}/api/knowledge/${projectId}`, { headers });
  return data;
};

export interface ProjectKnowledgeInput {
  projectId: string;
  type: ProjectKnowledgeType;
  title: string;
  content: string;
  tags?: string[];
  // Only meaningful when type === 'adr'. The server replaces this object wholesale on every
  // upsert — callers must resend the doc's existing adrMeta on any edit, not just when changing it
  // (see ProjectKnowledge.adrMeta's doc comment).
  adrMeta?: AdrMeta;
}

// Same endpoint handles create and update — omit `_id` to create, include it to update the
// existing doc in place (server verifies the caller owns it; see mflow-msa-mono
// `KnowledgeRepository.upsert()`).
export const upsertProjectKnowledge = async (
  input: ProjectKnowledgeInput & { _id?: string },
): Promise<ProjectKnowledge> => {
  const headers = await getHeaders();
  const { data } = await axios.post<ProjectKnowledge>(`${API_BASE_URL}/api/knowledge/`, input, { headers });
  return data;
};

export const deleteProjectKnowledge = async (id: string): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/knowledge/${id}`, { headers });
};
